import React from 'react';
import { Helmet } from "react-helmet-async";
import { Link } from 'react-router-dom';
import { Star, Phone, MapPin, Award, Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { companyInfo, certification, services, whyChooseUs } from '../mock';

const Home = () => {
  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <Helmet>
  <title>PT Ids Sun Teknik | Kontraktor Mekanikal & Elektrikal Profesional</title>
  <meta
    name="description"
    content="PT Ids Sun Teknik adalah kontraktor mekanikal dan elektrikal profesional bersertifikasi ISO untuk proyek industri dan gedung di Indonesia."
  />
  <link
    rel="canonical"
    href="https://www.ptidssunteknik.com/"
  />
</Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img 
            src={companyInfo.heroImages[0]} 
            alt="Industrial Construction"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/70 to-slate-900/80"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
              <Award className="w-4 h-4 mr-2 inline" />
              Bersertifikat {certification.standard}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {companyInfo.name}
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-4 font-medium">
              {companyInfo.tagline}
            </p>
            
            <p className="text-lg text-slate-200 mb-8 max-w-2xl mx-auto">
              Kontraktor profesional untuk solusi mekanikal dan elektrikal dengan standar kualitas internasional
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
              <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400 mr-2" />
                <span className="font-bold text-slate-900 text-lg">{companyInfo.rating}</span>
                <span className="text-slate-600 ml-2">({companyInfo.reviewCount} ulasan)</span>
              </div>
              
              <div className="flex items-center bg-white px-6 py-3 rounded-full shadow-md">
                <Shield className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-slate-700 font-semibold">ISO {certification.standard}</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300" onClick={scrollToContact}>
                Hubungi Kami
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-slate-300 hover:border-blue-600 text-slate-700 hover:text-blue-600 px-8 py-6 text-lg rounded-lg transition-all duration-300" asChild>
                <Link to="/layanan">Lihat Layanan</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4">
                  <CheckCircle className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2 text-lg">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Layanan Kami</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Solusi lengkap untuk kebutuhan konstruksi mekanikal dan elektrikal Anda
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {services.slice(0, 6).map((service) => (
              <Card key={service.id} className="bg-white border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 group overflow-hidden">
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">{service.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center">
            <Button size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-6 text-lg rounded-lg transition-all duration-300" asChild>
              <Link to="/layanan">Lihat Semua Layanan</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-section" className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Siap Memulai Proyek Anda?</h2>
            <p className="text-xl mb-10 text-blue-100">
              Hubungi kami sekarang untuk konsultasi gratis dan dapatkan penawaran terbaik
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <a href={`tel:${companyInfo.phone}`} className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 rounded-lg font-semibold text-lg flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl">
                <Phone className="w-5 h-5 mr-3" />
                {companyInfo.phone}
              </a>
              
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg rounded-lg transition-all duration-300" asChild>
                <Link to="/kontak">
                  <MapPin className="w-5 h-5 mr-3" />
                  Lihat Lokasi
                </Link>
              </Button>
            </div>
            
            <p className="text-blue-200 text-sm">
              Jam Operasional: {companyInfo.businessHours}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
